// Anti-transpercement vêtements / peau, calculé à l'ajustement (aucun coût par image).
//
// MakeHuman place chaque sommet de vêtement à  p = Σ w·base[ref] + offset. Sur les vêtements moulants l'offset
// est quasi nul et, dès que la morphologie s'écarte du modèle d'origine (poitrine, poids, musculature) ou que la
// pose étire la peau, la peau ressort à travers le tissu. On garantit donc, au repos et pour la forme courante :
//   1. chaque sommet de vêtement est au moins à GAP_OUT au-dessus de la peau qui le porte (le long de sa normale) ;
//   2. chaque sommet de peau recouvert est au moins à GAP_IN sous le tissu (enfoncement plafonné).
// Les marges absorbent les écarts de skinning en pose.

export const GAP_OUT = 0.002;   // 2 mm : le tissu ne colle jamais à la peau
export const GAP_IN = 0.0045;   // 4,5 mm : la peau reste sous le tissu même quand la pose l'étire un peu
export const MAX_PUSH_IN = 0.014;

/**
 * Pour chaque sommet de base hors du corps (géométries d'aide MakeHuman : collants, jupe, cheveux…),
 * indice du sommet du corps le plus proche — grille spatiale, calcul unique.
 */
export function nearestBodyMap(pos: Float32Array, bodyVerts: Uint32Array, nVerts: number): Int32Array {
  const map = new Int32Array(nVerts).fill(-1);
  const isBody = new Uint8Array(nVerts);
  for (const v of bodyVerts) { isBody[v] = 1; map[v] = v; }
  const cell = 0.03;
  const grid = new Map<string, number[]>();
  const key = (x: number, y: number, z: number) => `${Math.floor(x / cell)},${Math.floor(y / cell)},${Math.floor(z / cell)}`;
  for (const v of bodyVerts) {
    const k = key(pos[v * 3], pos[v * 3 + 1], pos[v * 3 + 2]);
    let l = grid.get(k);
    if (!l) grid.set(k, (l = []));
    l.push(v);
  }
  for (let v = 0; v < nVerts; v++) {
    if (isBody[v]) continue;
    const x = pos[v * 3], y = pos[v * 3 + 1], z = pos[v * 3 + 2];
    const cx = Math.floor(x / cell), cy = Math.floor(y / cell), cz = Math.floor(z / cell);
    let best = -1, bestD = Infinity;
    for (let r = 1; r <= 3 && best < 0; r++) {
      for (let i = -r; i <= r; i++) for (let j = -r; j <= r; j++) for (let k = -r; k <= r; k++) {
        const l = grid.get(`${cx + i},${cy + j},${cz + k}`);
        if (!l) continue;
        for (const b of l) {
          const dx = pos[b * 3] - x, dy = pos[b * 3 + 1] - y, dz = pos[b * 3 + 2] - z;
          const d = dx * dx + dy * dy + dz * dz;
          if (d < bestD) { bestD = d; best = b; }
        }
      }
    }
    map[v] = best;
  }
  return map;
}

export interface ClothBinding {
  /** Positions des sommets du vêtement (modifiées en place par pushOut). */
  pos: Float32Array;
  ref: Uint16Array;
  refW: Float32Array;
  nVerts: number;
}

/**
 * 1. Repousse le vêtement au-dessus de la peau. Renvoie true si au moins un sommet a bougé.
 * La normale de peau est la moyenne pondérée des normales des sommets de corps référencés.
 */
export function pushClothOut(c: ClothBinding, base: Float32Array, nrm: Float32Array, toBody: Int32Array): boolean {
  let moved = false;
  for (let i = 0; i < c.nVerts; i++) {
    const o = i * 3;
    let sx = 0, sy = 0, sz = 0, nx = 0, ny = 0, nz = 0, wsum = 0;
    for (let k = 0; k < 3; k++) {
      const w = c.refW[o + k];
      if (w <= 0) continue;
      const b = toBody[c.ref[o + k]];
      if (b < 0) continue;
      sx += w * base[b * 3]; sy += w * base[b * 3 + 1]; sz += w * base[b * 3 + 2];
      nx += w * nrm[b * 3]; ny += w * nrm[b * 3 + 1]; nz += w * nrm[b * 3 + 2];
      wsum += w;
    }
    if (wsum < 0.5) continue;
    sx /= wsum; sy /= wsum; sz /= wsum;
    const nl = Math.hypot(nx, ny, nz);
    if (nl < 1e-6) continue;
    nx /= nl; ny /= nl; nz /= nl;
    const d = (c.pos[o] - sx) * nx + (c.pos[o + 1] - sy) * ny + (c.pos[o + 2] - sz) * nz;
    // Seuls les sommets proches de la peau (vêtements moulants) sont concernés : une jupe ample n'est pas touchée.
    if (d < GAP_OUT && d > -0.03) {
      const push = GAP_OUT - d;
      c.pos[o] += push * nx; c.pos[o + 1] += push * ny; c.pos[o + 2] += push * nz;
      moved = true;
    }
  }
  return moved;
}

/**
 * 2. Profondeur d'enfoncement de la peau sous chaque vêtement (par sommet de base du corps).
 * `depth` est accumulé (max) sur tous les vêtements portés.
 */
export function accumulateSkinDepth(c: ClothBinding, base: Float32Array, nrm: Float32Array, toBody: Int32Array, depth: Float32Array) {
  for (let i = 0; i < c.nVerts; i++) {
    const o = i * 3;
    const px = c.pos[o], py = c.pos[o + 1], pz = c.pos[o + 2];
    for (let k = 0; k < 3; k++) {
      if (c.refW[o + k] < 0.05) continue;
      const b = toBody[c.ref[o + k]];
      if (b < 0) continue;
      const bx = base[b * 3], by = base[b * 3 + 1], bz = base[b * 3 + 2];
      const nx = nrm[b * 3], ny = nrm[b * 3 + 1], nz = nrm[b * 3 + 2];
      // Distance latérale : on ne considère que le tissu qui recouvre vraiment ce point de peau.
      const dx = px - bx, dy = py - by, dz = pz - bz;
      const s = dx * nx + dy * ny + dz * nz;
      const lat2 = dx * dx + dy * dy + dz * dz - s * s;
      if (lat2 > 0.018 * 0.018 || s > 0.03) continue;
      const need = GAP_IN - s;
      if (need > depth[b]) depth[b] = Math.min(need, MAX_PUSH_IN);
    }
  }
}

/**
 * Adoucit l'enfoncement autour des zones recouvertes (pas de "marche" visible au bord d'une manche) :
 * deux passes de dilatation décroissante sur la topologie du corps.
 */
export function featherDepth(depth: Float32Array, tris: Uint32Array, passes: number[] = [0.55, 0.3]) {
  const tmp = new Float32Array(depth.length);
  for (const k of passes) {
    tmp.set(depth);
    for (let t = 0; t < tris.length; t += 3) {
      const a = tris[t], b = tris[t + 1], c = tris[t + 2];
      const m = Math.max(depth[a], depth[b], depth[c]) * k;
      if (tmp[a] < m) tmp[a] = m;
      if (tmp[b] < m) tmp[b] = m;
      if (tmp[c] < m) tmp[c] = m;
    }
    depth.set(tmp);
  }
}

export interface ClothSurface extends ClothBinding {
  /** Triangles du vêtement (indices de sommets .obj). */
  tris: Uint32Array;
}

export const MAX_COMPRESS = 0.035;

/**
 * 2bis. Test peau ↔ SURFACE du vêtement : un vêtement moulant à maillage grossier tend des triangles qui
 * coupent la courbure du corps (effet "corde"), même si chacun de ses sommets est bien au-dessus de la peau.
 * Pour chaque sommet de peau, on lance un rayon le long de sa normale (±4 cm) vers les triangles proches
 * (grille spatiale) ; si le tissu est trop près ou derrière la peau, on enfonce la peau (compression).
 */
export function accumulateSkinDepthSurface(c: ClothSurface, base: Float32Array, nrm: Float32Array, bodyVerts: Uint32Array, depth: Float32Array) {
  const P = c.pos, T = c.tris, reach = 0.04;
  // Grille fine (1 cm) : chaque triangle n'occupe que les cellules de sa boîte ; chaque sommet de peau ne visite que les
  // cellules traversées par son segment ±reach le long de la normale. (Des chaussures à 57 000 triangles passaient de
  // 22 millions de tests à moins d'un million.)
  const cell = 0.01;
  const hash = (i: number, j: number, k: number) => ((i * 73856093) ^ (j * 19349663) ^ (k * 83492791)) | 0;
  // Peau candidate : près du vêtement seulement (boîte englobante élargie).
  let x0 = Infinity, y0 = Infinity, z0 = Infinity, x1 = -Infinity, y1 = -Infinity, z1 = -Infinity;
  for (let i = 0; i < P.length; i += 3) {
    if (P[i] < x0) x0 = P[i]; if (P[i] > x1) x1 = P[i];
    if (P[i + 1] < y0) y0 = P[i + 1]; if (P[i + 1] > y1) y1 = P[i + 1];
    if (P[i + 2] < z0) z0 = P[i + 2]; if (P[i + 2] > z1) z1 = P[i + 2];
  }
  const cand: number[] = [];
  for (const v of bodyVerts) {
    const ox = base[v * 3], oy = base[v * 3 + 1], oz = base[v * 3 + 2];
    if (ox < x0 - reach || ox > x1 + reach || oy < y0 - reach || oy > y1 + reach || oz < z0 - reach || oz > z1 + reach) continue;
    if (nrm[v * 3] === 0 && nrm[v * 3 + 1] === 0 && nrm[v * 3 + 2] === 0) continue;
    cand.push(v);
  }
  if (!cand.length) return;
  // Cellules visitées par les segments des sommets candidats (échantillonnés tous les demi-centimètres).
  const steps = Math.ceil((2 * reach) / (cell * 0.5));
  const vcells: number[][] = [];
  const wanted = new Set<number>();
  for (const v of cand) {
    const ox = base[v * 3], oy = base[v * 3 + 1], oz = base[v * 3 + 2];
    const dx = nrm[v * 3], dy = nrm[v * 3 + 1], dz = nrm[v * 3 + 2];
    const list: number[] = [];
    let prev = NaN;
    for (let k = 0; k <= steps; k++) {
      const sdist = -reach + (2 * reach * k) / steps;
      const h = hash(Math.floor((ox + dx * sdist) / cell), Math.floor((oy + dy * sdist) / cell), Math.floor((oz + dz * sdist) / cell));
      if (h === prev) continue;
      prev = h;
      list.push(h);
      wanted.add(h);
    }
    vcells.push(list);
  }
  // Triangles rangés par cellule (seulement les cellules visitées), normales précalculées.
  const grid = new Map<number, number[]>();
  const nt = T.length / 3;
  const tn = new Float32Array(nt * 3);
  for (let t = 0; t < nt; t++) {
    const a = T[t * 3] * 3, b = T[t * 3 + 1] * 3, d = T[t * 3 + 2] * 3;
    const ux = P[b] - P[a], uy = P[b + 1] - P[a + 1], uz = P[b + 2] - P[a + 2];
    const vx = P[d] - P[a], vy = P[d + 1] - P[a + 1], vz = P[d + 2] - P[a + 2];
    const nx = uy * vz - uz * vy, ny = uz * vx - ux * vz, nz = ux * vy - uy * vx;
    const l = Math.sqrt(nx * nx + ny * ny + nz * nz) || 1;
    tn[t * 3] = nx / l; tn[t * 3 + 1] = ny / l; tn[t * 3 + 2] = nz / l;
    const i0 = Math.floor(Math.min(P[a], P[b], P[d]) / cell), i1 = Math.floor(Math.max(P[a], P[b], P[d]) / cell);
    const j0 = Math.floor(Math.min(P[a + 1], P[b + 1], P[d + 1]) / cell), j1 = Math.floor(Math.max(P[a + 1], P[b + 1], P[d + 1]) / cell);
    const k0 = Math.floor(Math.min(P[a + 2], P[b + 2], P[d + 2]) / cell), k1 = Math.floor(Math.max(P[a + 2], P[b + 2], P[d + 2]) / cell);
    for (let i = i0; i <= i1; i++) for (let j = j0; j <= j1; j++) for (let k = k0; k <= k1; k++) {
      const h = hash(i, j, k);
      if (!wanted.has(h)) continue;
      let l2 = grid.get(h);
      if (!l2) grid.set(h, (l2 = []));
      l2.push(t);
    }
  }
  const stamp = new Int32Array(nt).fill(-1);
  for (let ci = 0; ci < cand.length; ci++) {
    const v = cand[ci];
    const ox = base[v * 3], oy = base[v * 3 + 1], oz = base[v * 3 + 2];
    const dx = nrm[v * 3], dy = nrm[v * 3 + 1], dz = nrm[v * 3 + 2];
    let best = Infinity;
    for (const h of vcells[ci]) {
      const list = grid.get(h);
      if (!list) continue;
      for (const t of list) {
        if (stamp[t] === ci) continue;
        stamp[t] = ci;
        // Orientation compatible (le tissu "regarde" dans le même sens que la peau).
        if (tn[t * 3] * dx + tn[t * 3 + 1] * dy + tn[t * 3 + 2] * dz < 0.25) continue;
        const a = T[t * 3] * 3, b = T[t * 3 + 1] * 3, d = T[t * 3 + 2] * 3;
        // Möller–Trumbore (droite paramétrée o + s·n).
        const e1x = P[b] - P[a], e1y = P[b + 1] - P[a + 1], e1z = P[b + 2] - P[a + 2];
        const e2x = P[d] - P[a], e2y = P[d + 1] - P[a + 1], e2z = P[d + 2] - P[a + 2];
        const px = dy * e2z - dz * e2y, py = dz * e2x - dx * e2z, pz = dx * e2y - dy * e2x;
        const det = e1x * px + e1y * py + e1z * pz;
        if (Math.abs(det) < 1e-12) continue;
        const inv = 1 / det;
        const tx = ox - P[a], ty = oy - P[a + 1], tz = oz - P[a + 2];
        const u = (tx * px + ty * py + tz * pz) * inv;
        if (u < 0 || u > 1) continue;
        const qx = ty * e1z - tz * e1y, qy = tz * e1x - tx * e1z, qz = tx * e1y - ty * e1x;
        const w = (dx * qx + dy * qy + dz * qz) * inv;
        if (w < 0 || u + w > 1) continue;
        const sd = (e2x * qx + e2y * qy + e2z * qz) * inv;
        if (sd < -reach || sd > reach) continue;
        if (Math.abs(sd) < Math.abs(best)) best = sd;
      }
    }
    if (best === Infinity) continue;
    const need = GAP_IN - best;
    if (need > depth[v]) depth[v] = Math.min(need, MAX_COMPRESS);
  }
}
